const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const authHeaders = (token) => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${token}`,
});

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Parse a response body, tolerating non-JSON error pages. A cold-starting or
// crashed backend (Render, proxies) can return an HTML 502/503 — calling
// res.json() on that throws a cryptic "Unexpected token '<'". Guard against it.
const parseBody = async (res) => {
  if (res.status === 204) return null;
  const ct = res.headers.get('content-type') || '';
  if (ct.includes('application/json')) {
    try { return await res.json(); } catch { return null; }
  }
  const text = await res.text().catch(() => '');
  return text ? { _raw: text } : null;
};

const RETRIABLE_STATUS = new Set([502, 503, 504]);

/**
 * Core request wrapper.
 *  - AbortController timeout (default 30s) so a hung request never blocks forever.
 *  - One retry with backoff on network errors / timeouts / gateway errors for
 *    idempotent GETs — this rides out a backend cold start transparently.
 *  - Content-type-aware error parsing so HTML error pages surface a clean message.
 */
const request = async (path, opts = {}) => {
  const {
    method = 'GET',
    token,
    body,
    headers,
    timeoutMs = 30000,
    retries = method === 'GET' ? 1 : 0,
    credentials,
    raw = false, // return the raw Response (for blob downloads)
  } = opts;

  const reqHeaders = headers || (token ? authHeaders(token) : { 'Content-Type': 'application/json' });

  let attempt = 0;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const res = await fetch(`${BASE}${path}`, {
        method,
        headers: reqHeaders,
        body,
        credentials,
        signal: controller.signal,
      });

      if (!res.ok) {
        if (RETRIABLE_STATUS.has(res.status) && attempt < retries) {
          attempt++;
          await sleep(1500 * attempt);
          continue;
        }
        const data = await parseBody(res);
        // Routes return a short `error` plus a `details` field carrying the
        // underlying cause — the provider's rejection reason, a config problem.
        // Dropping `details` turned every failure into "Test send failed" with
        // no way to tell one failure from another, so it is appended
        // when it adds something the headline does not already say.
        const headline = (data && data.error) || `Server unavailable (HTTP ${res.status})`;
        const detail = data && data.details;
        const detailText = typeof detail === 'string' ? detail : detail ? JSON.stringify(detail) : '';
        const err = new Error(
          detailText && !headline.includes(detailText) ? `${headline} — ${detailText}` : headline
        );
        if (data && data.code) err.code = data.code;
        if (data && data.problems) err.problems = data.problems;
        throw err;
      }

      return raw ? res : await parseBody(res);
    } catch (err) {
      const isAbort = err.name === 'AbortError';
      const isNetwork = err.name === 'TypeError';
      if ((isAbort || isNetwork) && attempt < retries) {
        attempt++;
        await sleep(1500 * attempt);
        continue;
      }
      if (isAbort) throw new Error('Request timed out — the server may be waking up. Please try again.');
      throw err;
    } finally {
      clearTimeout(timer);
    }
  }
};

// Thin verb helpers over `request`.
const get = (path, token) => request(path, { token });
const post = (path, token, payload) =>
  request(path, { method: 'POST', token, body: payload !== undefined ? JSON.stringify(payload) : undefined });
const put = (path, token, payload) =>
  request(path, { method: 'PUT', token, body: payload !== undefined ? JSON.stringify(payload) : undefined });
const del = (path, token) => request(path, { method: 'DELETE', token });

// ── Zoho (Mailer) ─────────────────────────────────────────────────────────────
export const apiCheckZohoStatus = (token) => get('/mailer/status', token);
export const apiZohoAuthUrl = (token) => get('/mailer/auth-url', token);
export const apiZohoExchangeToken = (token, code) => post('/mailer/exchange-token', token, { code });
export const apiZohoDisconnect = (token) => post('/mailer/disconnect', token);
export const apiZohoFromAddresses = (token) => get('/mailer/from-addresses', token);
export const apiZohoSendEmail = (token, payload) => post('/mailer/send-email', token, payload);

// ── Mailer settings, health and scheduler ─────────────────────────────────────
export const apiMailerSettings = (token) => get('/mailer/settings', token);
export const apiUpdateMailerSettings = (token, payload) =>
  request('/mailer/settings', { method: 'PATCH', token, body: JSON.stringify(payload) });
export const apiDeliverability = (token, domain) =>
  get(`/mailer/deliverability${domain ? `?domain=${encodeURIComponent(domain)}` : ''}`, token);

export const apiMailerTick = (token) => post('/mailer/tick', token);

// ── Mailboxes: the sending pool ───────────────────────────────────────────────
// One screen's worth of state comes back from apiMailboxes: the connected Zoho
// logins, every address under them, and the capacity arithmetic that says how
// much of the daily total is already claimed by running campaigns.
export const apiMailboxes = (token) => get('/mailboxes', token);
export const apiMailboxCapacity = (token) => get('/mailboxes/capacity', token);
export const apiMailboxConnectUrl = (token, region) =>
  get(`/mailboxes/connect-url${region ? `?region=${encodeURIComponent(region)}` : ''}`, token);
export const apiMailboxConnect = (token, payload) => post('/mailboxes/connections', token, payload);
export const apiMailboxVerifyConnection = (token, id) => post(`/mailboxes/connections/${id}/verify`, token);
export const apiMailboxDiscover = (token, id) => post(`/mailboxes/connections/${id}/discover`, token);
export const apiMailboxDisconnect = (token, id) => post(`/mailboxes/connections/${id}/disconnect`, token);
export const apiAddMailbox = (token, payload) => post('/mailboxes', token, payload);
export const apiUpdateMailbox = (token, id, payload) =>
  request(`/mailboxes/${id}`, { method: 'PATCH', token, body: JSON.stringify(payload) });
export const apiRemoveMailbox = (token, id) => del(`/mailboxes/${id}`, token);
export const apiTestMailbox = (token, id, payload) => post(`/mailboxes/${id}/test`, token, payload);
/**
 * A DNS audit, with its own timeout and no retry.
 *
 * The generic GET wrapper retries once on timeout, which for a 30s default
 * meant a slow lookup left the button spinning for over a minute with no way to
 * tell it from a hang. The server now bounds the audit at 12s, so anything past
 * 20s here is genuinely wrong and should say so rather than silently try again.
 */
export const apiMailboxHealth = (token, id) =>
  request(`/mailboxes/${id}/health`, { token, timeoutMs: 20000, retries: 0 });
export const apiMailboxActivity = (token, id, params) =>
  get(`/mailboxes/${id}/activity${qs(params)}`, token);

// ── HTML email ────────────────────────────────────────────────────────────────
// The preview runs the real sanitiser server-side rather than trusting the
// browser: the browser will happily render a data: image that Gmail refuses to.
export const apiHtmlPreview = (token, payload) => post('/campaigns/html-preview', token, payload);

/**
 * Upload one image for an HTML email and get back a permanent public URL.
 *
 * Deliberately not routed through the generic file uploader: those objects are
 * private and reached through a URL that expires in fifteen minutes, which is
 * useless inside a message somebody opens next week.
 */
export const apiUploadEmailImage = async (token, file) => {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`${BASE}/mailer/images`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: form,
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new Error(data?.error || 'Image upload failed');
  return data;
};

// ── Contacts ──────────────────────────────────────────────────────────────────
const qs = (params) => {
  const search = new URLSearchParams(
    Object.entries(params || {}).filter(([, v]) => v !== undefined && v !== null && v !== '')
  ).toString();
  return search ? `?${search}` : '';
};

export const apiGetContacts = (token, params) => get(`/contacts${qs(params)}`, token);
export const apiContactStats = (token) => get('/contacts/stats', token);
export const apiContactLists = (token) => get('/contacts/lists', token);
export const apiGetContact = (token, id) => get(`/contacts/${id}`, token);
export const apiCreateContact = (token, payload) => post('/contacts', token, payload);
export const apiUpdateContact = (token, id, payload) =>
  request(`/contacts/${id}`, { method: 'PATCH', token, body: JSON.stringify(payload) });
export const apiSuppressContact = (token, id) => post(`/contacts/${id}/suppress`, token);
export const apiDeleteContact = (token, id) => del(`/contacts/${id}`, token);

// ── Address verification ──────────────────────────────────────────────────────
export const apiVerifyStats = (token) => get('/contacts/verify/stats', token);

/**
 * Verify one batch. The server caps how many it will do per request and reports
 * what is left, so the caller loops — a single request covering 20,000 contacts
 * would exceed any sensible timeout. The longer timeout here covers a batch of
 * 500 addresses whose DNS lookups are slow to answer.
 */
export const apiVerifyContacts = (token, payload) =>
  request('/contacts/verify', {
    method: 'POST',
    token,
    body: JSON.stringify(payload || {}),
    timeoutMs: 180000,
  });

export const apiVerifyContact = (token, id) => post(`/contacts/${id}/verify`, token);
export const apiApplySuggestion = (token, id) => post(`/contacts/${id}/apply-suggestion`, token);

/**
 * CSV upload. Sent as multipart so a large list never has to be base64'd
 * through a JSON body — Content-Type is left unset so the browser can add the
 * multipart boundary itself.
 */
export const apiImportContacts = (token, file, { listName = '', tags = '', dryRun = false } = {}) => {
  const form = new FormData();
  form.append('file', file);
  form.append('listName', listName);
  form.append('tags', tags);
  form.append('dryRun', String(dryRun));
  return request('/contacts/import', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: form,
    // A 400-row import now also resolves DNS for every distinct domain in the
    // file, so the ceiling is higher than the dedupe lookups alone needed.
    timeoutMs: 300000,
  });
};

// ── Campaigns ─────────────────────────────────────────────────────────────────
export const apiGetCampaigns = (token) => get('/campaigns', token);
export const apiGetCampaign = (token, id) => get(`/campaigns/${id}`, token);
export const apiCreateCampaign = (token, payload) => post('/campaigns', token, payload);
export const apiUpdateCampaign = (token, id, payload) =>
  request(`/campaigns/${id}`, { method: 'PATCH', token, body: JSON.stringify(payload) });
export const apiEnrollContacts = (token, id, payload) => post(`/campaigns/${id}/enroll`, token, payload);
export const apiCampaignEnrollments = (token, id, params) =>
  get(`/campaigns/${id}/enrollments${qs(params)}`, token);
export const apiStopCampaignEnrollment = (token, id, enrollmentId, reason = 'Disqualified manually') =>
  post(`/campaigns/${id}/enrollments/${enrollmentId}/stop`, token, { reason });
export const apiCampaignAudience = (token, id, params) =>
  get(`/campaigns/${id}/audience${qs(params)}`, token);
export const apiDisqualifyCampaignContacts = (token, id, contactIds) =>
  post(`/campaigns/${id}/audience/disqualify`, token, { contactIds });
export const apiStartCampaign = (token, id) => post(`/campaigns/${id}/start`, token);
export const apiPauseCampaign = (token, id) => post(`/campaigns/${id}/pause`, token);
export const apiPreviewCampaign = (token, id, payload) => post(`/campaigns/${id}/preview`, token, payload);
export const apiTestCampaign = (token, id, payload) => post(`/campaigns/${id}/test`, token, payload);
export const apiCampaignActivity = (token, id, params) => get(`/campaigns/${id}/activity${qs(params)}`, token);
export const apiArchiveCampaign = (token, id) => del(`/campaigns/${id}`, token);
// Which mailboxes a campaign sends from, and how much of each it may use.
// Its own call because it is the one edit that changes a live campaign's rate.
export const apiSetCampaignMailboxes = (token, id, mailboxAllocations) =>
  put(`/campaigns/${id}/mailboxes`, token, { mailboxAllocations });

// ── Inbox ─────────────────────────────────────────────────────────────────────
export const apiGetInbox = (token, params) => get(`/inbox${qs(params)}`, token);
export const apiInboxStats = (token) => get('/inbox/stats', token);
export const apiGetInboxMessage = (token, id) => get(`/inbox/${id}`, token);
export const apiSyncInbox = (token) => post('/inbox/sync', token);
export const apiMarkInboxRead = (token, id, read = true) => post(`/inbox/${id}/read`, token, { read });
export const apiArchiveInboxMessage = (token, id, archived = true) =>
  post(`/inbox/${id}/archive`, token, { archived });
export const apiReplyToInbox = (token, id, payload) => post(`/inbox/${id}/reply`, token, payload);

// ── Auth ──────────────────────────────────────────────────────────────────────
export const apiLogin = (email, password) =>
  request('/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
    credentials: 'include',
  });

// ── Admin ─────────────────────────────────────────────────────────────────────
export const apiGetClients = (token) => get('/admin/clients', token);
export const apiGetClient = (token, id) => get(`/admin/clients/${id}`, token);
export const apiRegisterClient = (token, payload) => post('/admin/clients', token, payload);
export const apiUpdateClient = (token, id, data) => put(`/admin/clients/${id}`, token, data);
export const apiDeleteClient = (token, id) => del(`/admin/clients/${id}`, token);
export const apiCreateClientGDriveFolder = (token, id) => post(`/admin/clients/${id}/gdrive`, token);

export const apiGetInternalUsers = (token) => get('/admin/users', token);
export const apiGetInternalUserActivity = (token, id, limit = 250) => get(`/admin/users/${id}/activity?limit=${limit}`, token);
export const apiCreateInternalUser = (token, payload) => post('/admin/users', token, payload);
export const apiUpdateInternalUser = (token, id, payload) => put(`/admin/users/${id}`, token, payload);

export const apiCreateProject = (token, clientId, payload) => post(`/admin/clients/${clientId}/projects`, token, payload);
export const apiUpdateProject = (token, projectId, payload) => put(`/admin/projects/${projectId}`, token, payload);
export const apiDeleteProject = (token, projectId) => del(`/admin/projects/${projectId}`, token);

export const apiAddMilestone = (token, projectId, payload) => post(`/admin/projects/${projectId}/milestones`, token, payload);
export const apiUpdateMilestone = (token, projectId, milestoneId, payload) =>
  put(`/admin/projects/${projectId}/milestones/${milestoneId}`, token, payload);
export const apiDeleteMilestone = (token, projectId, milestoneId) =>
  del(`/admin/projects/${projectId}/milestones/${milestoneId}`, token);
export const apiNotifyMilestone = (token, projectId, milestoneId, payload) =>
  post(`/admin/projects/${projectId}/milestones/${milestoneId}/notify`, token, payload);

export const apiCheckGDriveStatus = (token) => get('/gdrive/status', token);

// Calendar / public consultation scheduling (admin)
export const apiGetCalendar = (token) => get('/calendar/admin', token);
export const apiUpdateCalendarSettings = (token, payload) =>
  request('/calendar/admin/settings', { method: 'PATCH', token, body: JSON.stringify(payload) });
export const apiCalendarConnectUrl = (token) => get('/calendar/oauth/url', token);
export const apiCalendarDisconnect = (token) => post('/calendar/oauth/disconnect', token);
export const apiCancelConsultation = (token, id, reason = '') =>
  post(`/calendar/admin/bookings/${id}/cancel`, token, { reason });

// ── File uploads (Cloudflare R2, presigned direct-to-storage) ─────────────────
// Two-step flow: presign (get a signed PUT URL) → browser uploads to R2 →
// confirm (server verifies the object exists and records it). The actual PUT is
// done in src/uploads.js via XHR so we get real progress events.
export const apiPresignClientUpload = (token, meta) => post('/users/uploads/presign', token, meta);
export const apiConfirmClientUpload = (token, payload) => post('/users/uploads/confirm', token, payload);

export const apiPresignMilestoneUpload = (token, projectId, milestoneId, meta) =>
  post(`/admin/projects/${projectId}/milestones/${milestoneId}/uploads/presign`, token, meta);
export const apiConfirmMilestoneUpload = (token, projectId, milestoneId, payload) =>
  post(`/admin/projects/${projectId}/milestones/${milestoneId}/uploads/confirm`, token, payload);

export const apiPresignSharedUpload = (token, projectId, meta) =>
  post(`/admin/projects/${projectId}/shared-files/presign`, token, meta);
export const apiConfirmSharedUpload = (token, projectId, payload) =>
  post(`/admin/projects/${projectId}/shared-files/confirm`, token, payload);

// Returns { url } — a short-lived presigned GET URL for a private R2 object.
export const apiGetDownloadUrl = (token, key) => get(`/files/download?key=${encodeURIComponent(key)}`, token);
export const apiGetPreviewUrl = (token, key) => get(`/files/download?key=${encodeURIComponent(key)}&inline=1`, token);
export const apiGetR2Storage = (token) => get('/files/storage', token);

// ── Legacy multipart uploads (Google Drive) ───────────────────────────────────
// Superseded by the R2 presign/confirm flow above; retained only so the older
// (unrouted) portal screens keep compiling and as a transitional fallback.
const legacyUpload = async (path, token, formData) => {
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  const ct = res.headers.get('content-type') || '';
  const data = ct.includes('application/json') ? await res.json().catch(() => null) : null;
  if (!res.ok) throw new Error((data && data.error) || `Upload failed (HTTP ${res.status})`);
  return data;
};

export const apiClientUploadFile = (token, file, requestId = null) => {
  const formData = new FormData();
  formData.append('file', file);
  if (requestId) formData.append('requestId', requestId);
  return legacyUpload('/users/upload', token, formData);
};

export const apiUploadMilestoneFile = (token, projectId, milestoneId, files) => {
  const formData = new FormData();
  const fileArray = Array.isArray(files) ? files : [files];
  for (const file of fileArray) formData.append('files', file);
  return legacyUpload(`/admin/projects/${projectId}/milestones/${milestoneId}/upload`, token, formData);
};

export const apiUploadSharedFile = (token, projectId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return legacyUpload(`/admin/projects/${projectId}/shared-files`, token, formData);
};

export const apiCreateUploadRequest = (token, clientId, payload) =>
  post(`/admin/clients/${clientId}/upload-requests`, token, payload);
export const apiDeleteUploadRequest = (token, clientId, requestId) =>
  del(`/admin/clients/${clientId}/upload-requests/${requestId}`, token);

export const apiDeleteMilestoneFile = (token, projectId, milestoneId, fileId) =>
  del(`/admin/projects/${projectId}/milestones/${milestoneId}/files/${fileId}`, token);
export const apiNotifyMilestoneFiles = (token, projectId, milestoneId, payload) =>
  post(`/admin/projects/${projectId}/milestones/${milestoneId}/notify-files`, token, payload);

export const apiDeleteSharedFile = (token, projectId, fileId) =>
  del(`/admin/projects/${projectId}/shared-files/${fileId}`, token);
export const apiAdminDeleteClientFile = (token, clientId, fileId) =>
  del(`/admin/clients/${clientId}/files/${fileId}`, token);

// ── Client ────────────────────────────────────────────────────────────────────
export const apiGetMyProfile = (token) => get('/users/profile', token);
export const apiUpdateMyProfile = (token, payload) => put('/users/profile', token, payload);
export const apiUploadProfilePicture = (token, file) => {
  const form = new FormData();
  form.append('avatar', file);
  return request('/users/profile/avatar', {
    method: 'POST', token, body: form,
    headers: { Authorization: `Bearer ${token}` },
  });
};
export const apiDeleteProfilePicture = (token) => del('/users/profile/avatar', token);

export const apiClientFeedback = (token, projectId, milestoneId, payload) =>
  post(`/projects/${projectId}/milestones/${milestoneId}/feedback`, token, payload);

export const apiClientReplyMilestone = (token, projectId, milestoneId, reply) =>
  post(`/users/projects/${projectId}/milestones/${milestoneId}/reply`, token, { reply });

export const apiClientDeleteFile = (token, fileId) => del(`/users/files/${fileId}`, token);

// ── Finance ───────────────────────────────────────────────────────────────────
export const apiGetFinanceSummary = (token) => get('/finance/summary', token);

export const apiGetRevenue = (token) => get('/finance/revenue', token);
export const apiCreateRevenue = (token, payload) => post('/finance/revenue', token, payload);
export const apiUpdateRevenue = (token, id, payload) => put(`/finance/revenue/${id}`, token, payload);
export const apiDeleteRevenue = (token, id) => del(`/finance/revenue/${id}`, token);

export const apiGetSales = (token) => get('/finance/sales', token);
export const apiCreateSale = (token, payload) => post('/finance/sales', token, payload);
export const apiUpdateSale = (token, id, payload) => put(`/finance/sales/${id}`, token, payload);
export const apiDeleteSale = (token, id) => del(`/finance/sales/${id}`, token);

export const apiGetPipeline = (token) => get('/finance/pipeline', token);
export const apiCreatePipelineContract = (token, payload) => post('/finance/pipeline', token, payload);
export const apiUpdatePipelineContract = (token, id, payload) => put(`/finance/pipeline/${id}`, token, payload);
export const apiDeletePipelineContract = (token, id) => del(`/finance/pipeline/${id}`, token);

export const apiGetExpenses = (token) => get('/finance/expenses', token);
export const apiCreateExpense = (token, payload) => post('/finance/expenses', token, payload);
export const apiUpdateExpense = (token, id, payload) => put(`/finance/expenses/${id}`, token, payload);
export const apiDeleteExpense = (token, id) => del(`/finance/expenses/${id}`, token);

export const apiGetCashouts = (token) => get('/finance/cashouts', token);
export const apiCreateCashout = (token, payload) => post('/finance/cashouts', token, payload);
export const apiUpdateCashout = (token, id, payload) => put(`/finance/cashouts/${id}`, token, payload);
export const apiDeleteCashout = (token, id) => del(`/finance/cashouts/${id}`, token);

export const apiExportFinanceCsv = async (token, type) => {
  const res = await request(`/finance/export/${type}`, {
    token,
    headers: { Authorization: `Bearer ${token}` },
    raw: true,
  });
  return await res.blob();
};

// ── Messages / Threads (board, project, todo) ─────────────────────────────────
export const apiGetThread = (token, threadType, threadId, since = null) => {
  const params = new URLSearchParams({ threadType, threadId });
  if (since) params.set('since', since);
  return get(`/messages?${params.toString()}`, token);
};

export const apiGetMentionCandidates = (token, threadType, threadId) => {
  const params = new URLSearchParams({ threadType, threadId });
  return get(`/messages/mentions?${params.toString()}`, token);
};

export const apiPostMessage = (token, payload) => post('/messages', token, payload);

// Edit your own message — author-only, enforced server-side.
export const apiUpdateMessage = (token, id, body) => put(`/messages/${id}`, token, { body });

// Delete your own message — author-only, enforced server-side.
export const apiDeleteMessage = (token, id) => del(`/messages/${id}`, token);

// ── Pings (private and group conversations) ─────────────────────────────────
export const apiGetPingPeople = (token) => get('/pings/people', token);
export const apiGetPingConversations = (token) => get('/pings/conversations', token);
export const apiStartPrivatePing = (token, userId) => post('/pings/private', token, { userId });
export const apiCreatePingGroup = (token, payload) => post('/pings/groups', token, payload);
export const apiUpdatePingGroup = (token, conversationId, payload) => put(`/pings/conversations/${conversationId}`, token, payload);
export const apiDeletePingGroup = (token, conversationId) => del(`/pings/conversations/${conversationId}`, token);
export const apiGetPingMessages = (token, conversationId, since = null) => get(`/pings/conversations/${conversationId}/messages${since ? `?since=${encodeURIComponent(since)}` : ''}`, token);
export const apiSendPingMessage = (token, conversationId, payload) => post(`/pings/conversations/${conversationId}/messages`, token, payload);
export const apiMarkPingRead = (token, conversationId) => post(`/pings/conversations/${conversationId}/read`, token);
export const apiPresignPingAttachment = (token, conversationId, meta) => post(`/pings/conversations/${conversationId}/uploads/presign`, token, meta);
export const apiPresignPingLockerFile = (token, conversationId, meta) => post(`/pings/conversations/${conversationId}/locker/presign`, token, meta);
export const apiConfirmPingLockerFile = (token, conversationId, payload) => post(`/pings/conversations/${conversationId}/locker/confirm`, token, payload);
export const apiDeletePingLockerFile = (token, conversationId, fileId) => del(`/pings/conversations/${conversationId}/locker/${fileId}`, token);

// ── Team (admin) ──────────────────────────────────────────────────────────────
export const apiGetTeam = (token) => get('/admin/team', token);

// ── To-dos (admin only) ───────────────────────────────────────────────────────
export const apiGetTodos = (token, clientId, projectId = '') => get(`/todos?client=${clientId}${projectId ? `&project=${projectId}` : ''}`, token);
export const apiCreateTodoList = (token, payload) => post('/todos', token, payload);
export const apiUpdateTodoList = (token, id, payload) => put(`/todos/${id}`, token, payload);
export const apiDeleteTodoList = (token, id) => del(`/todos/${id}`, token);
export const apiAddTodoSection = (token, listId, payload) => post(`/todos/${listId}/sections`, token, payload);
export const apiUpdateTodoSection = (token, listId, sectionId, payload) => put(`/todos/${listId}/sections/${sectionId}`, token, payload);
export const apiDeleteTodoSection = (token, listId, sectionId) => del(`/todos/${listId}/sections/${sectionId}`, token);
export const apiAddTodoItem = (token, listId, payload) => post(`/todos/${listId}/items`, token, payload);
export const apiUpdateTodoItem = (token, listId, itemId, payload) => put(`/todos/${listId}/items/${itemId}`, token, payload);
export const apiDeleteTodoItem = (token, listId, itemId) => del(`/todos/${listId}/items/${itemId}`, token);

// ── Notifications ─────────────────────────────────────────────────────────────
export const apiGetNotifications = (token) => get('/notifications', token);
export const apiMarkNotificationRead = (token, id) => put(`/notifications/${id}/read`, token);
export const apiMarkAllNotificationsRead = (token) => put('/notifications/read-all', token);
export const apiUpdateNotificationReminder = (token, id, payload) => put(`/notifications/${id}/reminder`, token, payload);
export const apiDismissNotification = (token, id) => del(`/notifications/${id}`, token);
export const apiGetPushPublicKey = (token) => get('/notifications/push/public-key', token);
export const apiSavePushSubscription = (token, subscription) => post('/notifications/push/subscribe', token, subscription);
export const apiDeletePushSubscription = (token, endpoint) => request('/notifications/push/subscribe', {
  method: 'DELETE',
  token,
  body: JSON.stringify({ endpoint }),
});

// Personal tasks and notes (scoped to the signed-in profile)
export const apiGetPersonalWorkspace = (token) => get('/personal-workspace', token);
export const apiCreatePersonalTask = (token, text) => post('/personal-workspace/tasks', token, { text });
export const apiUpdatePersonalTask = (token, taskId, payload) => put(`/personal-workspace/tasks/${taskId}`, token, payload);
export const apiDeletePersonalTask = (token, taskId) => del(`/personal-workspace/tasks/${taskId}`, token);
export const apiUpdatePersonalNotes = (token, notes) => put('/personal-workspace/notes', token, { notes });

// Private Space (internal-only folders, notes, collaborators, and files)
export const apiGetPrivateFolders = (token) => get('/private-space', token);
export const apiGetPrivateFolder = (token, folderId) => get(`/private-space/${folderId}`, token);
export const apiCreatePrivateFolder = (token, payload) => post('/private-space', token, payload);
export const apiUpdatePrivateFolder = (token, folderId, payload) => put(`/private-space/${folderId}`, token, payload);
export const apiUpdatePrivateFolderNotes = (token, folderId, notes) => put(`/private-space/${folderId}/notes`, token, { notes });
export const apiUpdatePrivateFolderAccess = (token, folderId, collaboratorIds) => put(`/private-space/${folderId}/access`, token, { collaboratorIds });
export const apiDeletePrivateFolder = (token, folderId) => del(`/private-space/${folderId}`, token);
export const apiPresignPrivateFile = (token, folderId, meta) => post(`/private-space/${folderId}/files/presign`, token, meta);
export const apiConfirmPrivateFile = (token, folderId, payload) => post(`/private-space/${folderId}/files/confirm`, token, payload);
export const apiDeletePrivateFile = (token, folderId, fileId) => del(`/private-space/${folderId}/files/${fileId}`, token);
